import React, {useEffect, useState} from 'react';
import classnames from 'classnames';
import {
    OnInputChangeEvent,
    OnSelectChangeEvent,
    NoteType,
    EditQcReportNoteBody,
} from '@pluto/@types';
import {curry} from 'ramda';
import wrap from 'wraptile';
import TextField from '@material-ui/core/TextField';
import {QCReportsNotesDelete} from './QCReportsNotesDelete';
import QCReportsReasons from '../qc-reports-reasons/QCReportsReasonsConnected';

export type SetEditedDescriptionType = (value: string) => void;
export type OnEditNoteType = (body: EditQcReportNoteBody) => void;
export type SetEditNoteIdType = (value: number | null) => void;

export interface QCReportsNoteProps {
    setEditNoteId: SetEditNoteIdType;
    editNoteId: number | null;
    noteType: NoteType;
    id: number;
    name: string;
    timespanCategoryId: number;
    lastUpdatedValue: string;
    onEditNote: OnEditNoteType;
    onDeleteClick: () => void;
    reasonLabel: string | undefined;
    reasonValue: string;
    jobDeliveryDate: string;
}

export const handleNoteClick = wrap((setEditNoteId: SetEditNoteIdType, id: number, jobDeliveryDate: string) => {
    if (!jobDeliveryDate) {
        setEditNoteId(id);
    }
});

export const handleDescriptionChange = curry((setEditedDescription: SetEditedDescriptionType, e: OnInputChangeEvent) => {
    setEditedDescription(e.target.value);
});

export const handleBlur = wrap((onEditNote: OnEditNoteType, value: string, timespanCategoryId: number) => {
    onEditNote({
        value,
        timespanCategoryId,
    });
});

export const handleChangeReason = curry((onEditNote: OnEditNoteType, value: string, e: OnSelectChangeEvent) => {
    onEditNote({
        value,
        timespanCategoryId: e.target.value as number,
    });
});

export const getQcReportReasonsLabel = (onEditNote: OnEditNoteType, editNoteId: number | null, id: number, editedDescription: string, noteType: NoteType, timespanCategoryId: number, reasonLabel: string | undefined) => {
    if (id === editNoteId) {
        return (
            <QCReportsReasons
                onChange={handleChangeReason(onEditNote, editedDescription)}
                selectReasonPlaceholder=""
                noteType={noteType}
                value={timespanCategoryId}
            />
        );
    }
    
    return reasonLabel;
};

export const getQcReportReasonsValue = (setEditedDescription: SetEditedDescriptionType, editedDescription: string, onEditNote: OnEditNoteType, timespanCategoryId: number, id: number, editNoteId: number | null, reasonValue: string) => {
    if (id === editNoteId) {
        return (
            <TextField
                multiline
                value={editedDescription}
                inputProps={{
                    maxLength: 1024,
                }}
                onChange={handleDescriptionChange(setEditedDescription)}
                onBlur={handleBlur(onEditNote, editedDescription, timespanCategoryId)}
            />
        );
    }
    
    return <pre>{reasonValue}</pre>;
};

export const checkNoteValue = (value: string, prevValue: string, editMode: boolean, timespanCategoryId: number, onEditNote: OnEditNoteType, handleBlur: (onEditNote: OnEditNoteType, value: string, timespanCategoryId: number) => () => void) => {
    if (value !== prevValue && !editMode) {
        handleBlur(onEditNote, value, timespanCategoryId)();
    }
};

export const QCReportsNote = (props: QCReportsNoteProps) => {
    const {
        id,
        setEditNoteId,
        editNoteId,
        name,
        lastUpdatedValue,
        timespanCategoryId,
        onDeleteClick,
        noteType,
        onEditNote,
        reasonLabel,
        reasonValue,
        jobDeliveryDate,
    } = props;
    const [editedDescription, setEditedDescription] = useState<string>(reasonValue);
    const editMode = id === editNoteId;
    useEffect(() => {
        checkNoteValue(
            editedDescription,
            reasonValue,
            editMode,
            timespanCategoryId,
            onEditNote,
            handleBlur,
        );
    }, [editMode, onEditNote, editedDescription, reasonValue, timespanCategoryId]);
    
    return (
        <div
            className={classnames('qc-reports__note', {
                'edit-mode': editMode && !jobDeliveryDate,
            })}
            onClick={handleNoteClick(setEditNoteId, id, jobDeliveryDate)}
            role="button"
        >
            <div className="qc-reports__note-name-wrapper">
                <div className="qc-reports__note-name-last-update">
                    <div className="qc-reports__note-name">
                        {name}
                    </div>
                    <div className="qc-reports__note-last-update">
                        {lastUpdatedValue}
                    </div>
                </div>
                <div className="qc-reports__note-delete">
                    {!jobDeliveryDate && (
                        <QCReportsNotesDelete
                            onDeleteClick={onDeleteClick}
                        />
                    )}
                </div>
            </div>
            <div className="qc-reports__note-reason-wrapper">
                <div className="qc-reports__note-reason">
                    {getQcReportReasonsLabel(
                        onEditNote,
                        editNoteId,
                        id,
                        editedDescription,
                        noteType,
                        timespanCategoryId,
                        reasonLabel,
                    )}
                </div>
                <div className="qc-reports__note-reason-value">
                    {getQcReportReasonsValue(
                        setEditedDescription,
                        editedDescription,
                        onEditNote,
                        timespanCategoryId,
                        id,
                        editNoteId,
                        reasonValue,
                    )}
                </div>
            </div>
        </div>
    );
};
